import React from 'react';
import { SUBSCRIPTION_PLANS } from './payment';

const PricingCard = ({ 
  plan, 
  isCurrentTier, 
  onSubscribe 
}: { 
  plan: typeof SUBSCRIPTION_PLANS[keyof typeof SUBSCRIPTION_PLANS];
  isCurrentTier: boolean;
  onSubscribe: () => void;
}) => {
  return (
    <div className="bg-slate-800 rounded-lg p-6 flex flex-col">
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-slate-100">{plan.name}</h3>
        <div className="mt-4">
          <span className="text-4xl font-bold text-slate-100">${plan.price}</span>
          <span className="text-slate-400 ml-2">/{plan.interval}</span>
        </div>
      </div>

      <ul className="mb-8 flex-grow">
        {plan.features.map((feature, index) => (
          <li key={index} className="flex items-center mb-3 text-slate-300">
            <svg
              className="w-5 h-5 text-emerald-500 mr-2 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
            {feature}
          </li>
        ))}
      </ul>

      <button
        onClick={onSubscribe}
        disabled={isCurrentTier}
        className={`w-full py-3 px-4 rounded-md font-medium ${
          isCurrentTier
            ? 'bg-emerald-900 text-emerald-300 cursor-not-allowed'
            : 'bg-emerald-500 text-slate-900 hover:bg-emerald-400'
        }`}
      >
        {isCurrentTier ? 'Current Plan' : 'Subscribe'}
      </button>
    </div>
  );
};

const SubscriptionDialog = ({
  isOpen,
  onClose,
  onSubscribe,
  currentTier
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: (planId: string, paymentMethod: 'stripe' | 'bch') => void;
  currentTier: 'free' | 'beta' | 'alpha';
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="fixed inset-0 bg-slate-900/80" 
        onClick={onClose}
      />
      
      <div className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl md:mx-4 flex flex-col">
        {/* Dialog Content */}
        <div className="relative flex-1 bg-slate-900 md:rounded-lg shadow-xl w-full overflow-hidden border border-slate-700 flex flex-col">
          {/* Scrollable Content Area */}
          <div className="overflow-y-auto flex-1">
            <div className="p-6">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-slate-100 mb-4">
                  Upgrade Your Experience
                </h2>
                <p className="text-slate-300">
                  Choose the plan that best fits your needs and start dominating life.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Free Tier */}
                <PricingCard
                  plan={{
                    id: 'free',
                    name: 'Free',
                    price: 0,
                    currency: 'USD',
                    interval: 'month',
                    features: [
                      '5 messages per hour',
                      'Basic support',
                      'Standard response length'
                    ]
                  }}
                  isCurrentTier={currentTier === 'free'}
                  onSubscribe={() => {}}
                />

                {/* Beta Tier */}
                <PricingCard
                  plan={SUBSCRIPTION_PLANS.beta}
                  isCurrentTier={currentTier === 'beta'}
                  onSubscribe={() => onSubscribe('beta', 'stripe')}
                />

                {/* Alpha Tier */}
                <PricingCard
                  plan={SUBSCRIPTION_PLANS.alpha}
                  isCurrentTier={currentTier === 'alpha'}
                  onSubscribe={() => onSubscribe('alpha', 'stripe')}
                />
              </div>

              {/* Payment Methods Section */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">
                  Payment Methods
                </h3>
                <div className="flex gap-4">
                  <button
                    onClick={() => onSubscribe(currentTier === 'free' ? 'beta' : 'alpha', 'stripe')}
                    className="flex items-center px-4 py-2 bg-slate-800 rounded-md hover:bg-slate-700"
                  >
                    <img 
                      src="/stripe-logo.svg" 
                      alt="Stripe" 
                      className="h-6" 
                    />
                  </button>
                  <button
                    onClick={() => onSubscribe(currentTier === 'free' ? 'beta' : 'alpha', 'bch')}
                    className="flex items-center px-4 py-2 bg-slate-800 rounded-md hover:bg-slate-700"
                  >
                    <img 
                      src="/bch-logo.svg" 
                      alt="Bitcoin Cash" 
                      className="h-6" 
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Close Button - Fixed at the top right */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-slate-400 hover:text-slate-200 p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDialog;
